import type { KeyQuestionDoc } from '../../redux/slices/advisory/advisoryTypes';
import type { SupervisorProposalState } from './useSupervisorTurn';
import { isEngagementEngine, type EngagementEngine } from './advisoryEngine';

export const SOURCE_STATUSES = [
  'ready',
  'awaitingUpload',
  'uploading',
  'queued',
  'fetching',
  'researching',
  'ingesting',
  'submissionUncertain',
  'failed',
  'insufficientEvidence',
  'ingestionFailed',
] as const;
export type WorkspaceSourceStatus = (typeof SOURCE_STATUSES)[number];
export interface WorkspaceSource {
  sourceId: string;
  generation: number;
  kind: 'profile' | 'url' | 'file';
  name: string;
  company: string | null;
  status: WorkspaceSourceStatus;
  documentId: string | null;
  errorCode: string | null;
  submittedUrl?: string;
  finalUrl?: string;
  fileName?: string;
  sizeBytes?: number;
  mimeType?: string;
}
export interface WorkspaceTask {
  taskId: string;
  kind: 'initialize' | 'profile' | 'source' | 'exploration' | 'answers';
  generation: number;
  status: string;
  phase: string;
  errorCode: string | null;
  startedAt?: string | null;
  updatedAt?: string;
}
export interface WorkspaceSnapshot {
  engine?: EngagementEngine;
  schemaVersion: 1;
  projectId: string;
  setupRevision: number;
  progressRevision: number;
  goal: string;
  questionsReady: boolean;
  locked: boolean;
  questions: KeyQuestionDoc[];
  questionProposals: SupervisorProposalState | null;
  sources: Record<string, WorkspaceSource>;
  tasks: Record<string, WorkspaceTask>;
  canStart: boolean;
  canStartWithGaps: boolean;
  blockers: Array<{ code: string; sourceId?: string; generation?: number }>;
  acceptedGaps: WorkspaceSource[];
  run: {
    runId: string;
    answerRunId: string;
    conversationId: string;
    status: string;
    engine?: EngagementEngine;
  } | null;
}
export type WorkspaceAction =
  | { operation: 'matchQuestion'; query: string }
  | { operation: 'acceptQuestion'; questionId: string; query: string }
  | { operation: 'removeQuestions'; questionIds: string[] }
  | { operation: 'addUrl'; url: string; company: string | null }
  | {
      operation: 'registerFile';
      fileName: string;
      sizeBytes: number;
      mimeType: string;
      company: string | null;
    }
  | { operation: 'retrySource'; sourceId: string }
  | { operation: 'retryProfile'; company: string; sourceUrl?: string }
  | { operation: 'resumeTask'; taskId: string }
  | { operation: 'start'; acknowledgedGaps: Array<{ sourceId: string; generation: number }> };
export type WorkspaceRequest = WorkspaceAction & {
  requestId: string;
  expectedSetupRevision: number;
};
export interface WorkspaceResult {
  requestId: string;
  operation: WorkspaceAction['operation'];
  sourceId?: string;
  documentId?: string;
  generation?: number;
  matching?: boolean;
  runId?: string;
}
const object = (v: unknown): v is Record<string, unknown> =>
  Boolean(v && typeof v === 'object' && !Array.isArray(v));
const integer = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) > 0;
const text = (v: unknown): v is string => typeof v === 'string';
const source = (v: unknown): v is WorkspaceSource =>
  object(v) &&
  text(v.sourceId) &&
  integer(v.generation) &&
  ['profile', 'url', 'file'].includes(String(v.kind)) &&
  text(v.name) &&
  (v.company === null || text(v.company)) &&
  (SOURCE_STATUSES as readonly unknown[]).includes(v.status) &&
  (v.documentId === null || text(v.documentId));

/** Reject unknown readiness data rather than accidentally enabling admission to analysis. */
export function parseWorkspaceSnapshot(raw: unknown, projectId: string): WorkspaceSnapshot | null {
  if (
    !object(raw) ||
    raw.schemaVersion !== 1 ||
    raw.projectId !== projectId ||
    (raw.engine !== undefined && !isEngagementEngine(raw.engine)) ||
    !integer(raw.setupRevision) ||
    !integer(raw.progressRevision) ||
    !text(raw.goal) ||
    typeof raw.questionsReady !== 'boolean' ||
    typeof raw.locked !== 'boolean' ||
    typeof raw.canStart !== 'boolean' ||
    typeof raw.canStartWithGaps !== 'boolean' ||
    !object(raw.sources) ||
    !object(raw.tasks) ||
    !Array.isArray(raw.blockers) ||
    !Array.isArray(raw.acceptedGaps)
  )
    return null;
  if (
    Object.entries(raw.sources).some(([id, v]) => !source(v) || v.sourceId !== id) ||
    !raw.acceptedGaps.every(source)
  )
    return null;
  if (
    Object.entries(raw.tasks).some(
      ([id, v]) =>
        !object(v) ||
        v.taskId !== id ||
        !integer(v.generation) ||
        !['initialize', 'profile', 'source', 'exploration', 'answers'].includes(String(v.kind)) ||
        !text(v.status) ||
        !text(v.phase)
    )
  )
    return null;
  if (raw.blockers.some(v => !object(v) || !text(v.code))) return null;
  const questions = raw.questions ?? [];
  if (
    !Array.isArray(questions) ||
    questions.some(
      q =>
        !object(q) ||
        !text(q.id) ||
        !text(q.question) ||
        (q.order !== undefined && typeof q.order !== 'number') ||
        (q.subquestions !== undefined &&
          (!Array.isArray(q.subquestions) || !q.subquestions.every(text)))
    )
  )
    return null;
  if (raw.questionsReady && !Array.isArray(raw.questions)) return null;
  if (
    raw.run !== null &&
    (!object(raw.run) ||
      (raw.run.engine !== undefined && !isEngagementEngine(raw.run.engine)) ||
      !text(raw.run.runId) ||
      !text(raw.run.answerRunId) ||
      !text(raw.run.conversationId) ||
      !text(raw.run.status))
  )
    return null;
  let questionProposals: SupervisorProposalState | null = null;
  const p = raw.questionProposals;
  if (p !== undefined && p !== null) {
    if (
      !object(p) ||
      !text(p.userQuery) ||
      typeof p.poorMatch !== 'boolean' ||
      !Array.isArray(p.proposals) ||
      p.proposals.some(
        v => !object(v) || !text(v.id) || !text(v.question) || !text(v.component) || !text(v.reason)
      )
    )
      return null;
    questionProposals = p as unknown as SupervisorProposalState;
  }
  return { ...raw, questions, questionProposals } as unknown as WorkspaceSnapshot;
}
export function parseWorkspaceResult(
  raw: unknown,
  request: WorkspaceRequest
): WorkspaceResult | null {
  if (!object(raw) || raw.requestId !== request.requestId || raw.operation !== request.operation)
    return null;
  if (request.operation === 'start' && !text(raw.runId)) return null;
  if (
    (request.operation === 'registerFile' || request.operation === 'addUrl') &&
    (!text(raw.sourceId) || !text(raw.documentId) || !integer(raw.generation))
  )
    return null;
  return raw as unknown as WorkspaceResult;
}
export function newerWorkspace(current: WorkspaceSnapshot | null, next: WorkspaceSnapshot) {
  if (!current) return next;
  return next.setupRevision >= current.setupRevision &&
    next.progressRevision >= current.progressRevision
    ? next
    : current;
}
export const isEvidenceGap = (status: WorkspaceSourceStatus) =>
  ['failed', 'insufficientEvidence', 'ingestionFailed'].includes(status);
